import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AlertCircle, FileCheck2, Trash2, UploadCloud } from 'lucide-react'
import { getBundles } from '@/api/bundles'
import { discardDraftFile, getDraftFile, putDraftFile } from '@/api/draftFiles'
import { getDiff } from '@/api/diff'
import { deleteDraftPrompt } from '@/api/prompts'
import { validateDraft } from '@/api/validation'
import { Banner } from '@/components/Banner'
import { ConfirmModal } from '@/components/ConfirmModal'
import { EmptyState } from '@/components/EmptyState'
import { PublishModal } from '@/components/PublishModal'
import { Tooltip } from '@/components/Tooltip'
import { useDraftWritesInFlight } from '@/hooks/useDraftWritesInFlight'
import { usePublishMutation } from '@/hooks/usePublishMutation'
import { errorMessage } from '@/lib/apiErrors'
import { fileKeyLabel } from '@/lib/fileKeys'
import { refreshAfterWrite } from '@/lib/refreshAfterWrite'
import type { FileKey } from '@/types/bundle'
import type { DiffChange, ValidationError } from '@/types/diff'
import { DiffFileBlock } from './components/DiffFileBlock'
import { DiffPromptBlock } from './components/DiffPromptBlock'
import { ValidationBanner, type ValidationJumpLink } from './components/ValidationBanner'
import { deleteValueAtPath, setValueAtPath } from './draftPatch'
import { humanizePath } from './pathLabel'

const PUBLISH_SUCCESS_REDIRECT_MS = 3000

function SkeletonBlock() {
  return <div className="h-96 animate-pulse rounded-control bg-surface-raised" />
}

const HIGHLIGHT_DURATION_MS = 1500

export function DiffValidationScreen() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const from = searchParams.get('from') ?? 'live'
  const to = searchParams.get('to') ?? 'draft'
  const isDraftMode = to === 'draft'

  const [discardModalOpen, setDiscardModalOpen] = useState(false)
  const [fileToDiscard, setFileToDiscard] = useState<FileKey | null>(null)
  const [promptToDiscard, setPromptToDiscard] = useState<string | null>(null)
  const [revertingKey, setRevertingKey] = useState<string | null>(null)
  const [highlightedAnchor, setHighlightedAnchor] = useState<string | null>(null)
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const [publishModalOpen, setPublishModalOpen] = useState(false)
  const [releaseNote, setReleaseNote] = useState('')
  const publishMutation = usePublishMutation()
  // Already warm from AppShell's own useQuery(['bundles']) on every screen -
  // this is not a new network fetch, just this screen reading the same
  // cache for the modal's version-preview string (Frontend PRD 08's own
  // "no new query" framing).
  const bundlesQuery = useQuery({ queryKey: ['bundles'], queryFn: getBundles })
  const liveVersion = bundlesQuery.data?.find((bundle) => bundle.isLive)?.version

  useEffect(() => {
    if (!publishMutation.isSuccess) return
    const timeout = setTimeout(() => navigate('/'), PUBLISH_SUCCESS_REDIRECT_MS)
    return () => clearTimeout(timeout)
  }, [publishMutation.isSuccess, navigate])

  const diffQuery = useQuery({ queryKey: ['diff', from, to], queryFn: () => getDiff(from, to) })
  // Historical (non-draft) compare mode never runs validate-draft - it
  // always validates the *current* draft, unrelated to whichever two
  // published versions happen to be selected here.
  const validateQuery = useQuery({
    queryKey: ['validate-draft'],
    queryFn: validateDraft,
    enabled: isDraftMode,
  })

  const discardMutation = useMutation({
    mutationFn: ({ fileKeys, promptFilenames }: { fileKeys: FileKey[]; promptFilenames: string[] }) =>
      Promise.all([
        ...fileKeys.map((fileKey) => discardDraftFile(fileKey)),
        ...promptFilenames.map((filename) => deleteDraftPrompt(filename)),
      ]),
    onSuccess: () => {
      refreshAfterWrite(queryClient, { kind: 'discard-all' })
      setDiscardModalOpen(false)
    },
  })

  // Prompt-file equivalent of discardFileMutation - one prompts/*.txt file's
  // entire draft edit, PRD 29's own DELETE (never a per-content-line revert,
  // since a prompt file has no path structure to revert one piece of).
  const discardPromptMutation = useMutation({
    mutationFn: (filename: string) => deleteDraftPrompt(filename),
    onSuccess: () => {
      refreshAfterWrite(queryClient, { kind: 'discard-prompt' })
      setPromptToDiscard(null)
    },
  })

  // "Whole group" level - discards one file's entire draft edit, same
  // DELETE PRD 21 exposes, just scoped to a single file_key instead of
  // every changed one.
  const discardFileMutation = useMutation({
    mutationFn: (fileKey: FileKey) => discardDraftFile(fileKey),
    onSuccess: (_result, fileKey) => {
      refreshAfterWrite(queryClient, { kind: 'draft-file', fileKey })
      setFileToDiscard(null)
    },
  })

  // "Per field" level - there is no backend endpoint for a single-field
  // revert (PRD 21's DELETE is whole-file only), so this reads the current
  // draft, reverses just this one leaf (draftPatch.ts), and PUTs the whole
  // file back - the same whole-file-replace every edit screen already does.
  const revertFieldMutation = useMutation({
    mutationFn: async ({ fileKey, change }: { fileKey: FileKey; change: DiffChange }) => {
      const current = await getDraftFile<Record<string, unknown>>(fileKey)
      const updated =
        change.oldValue === null
          ? deleteValueAtPath(current, change.path)
          : setValueAtPath(current, change.path, change.oldValue)
      return putDraftFile(fileKey, updated)
    },
    onSuccess: (_result, { fileKey }) => refreshAfterWrite(queryClient, { kind: 'draft-file', fileKey }),
    onSettled: () => setRevertingKey(null),
  })

  // A Save sent from another screen that has not been answered yet: a
  // discard or publish now would race it, and the late PUT would re-create
  // what was just discarded. This screen's own revert (also a PUT) is not
  // counted against itself.
  const writesInFlight = useDraftWritesInFlight()
  const writesLocked = writesInFlight - (revertFieldMutation.isPending ? 1 : 0) > 0

  function handleRevertField(fileKey: FileKey, change: DiffChange) {
    setRevertingKey(`${fileKey}::${change.path}`)
    revertFieldMutation.mutate({ fileKey, change })
  }

  if (diffQuery.isPending || (isDraftMode && validateQuery.isPending)) {
    return <SkeletonBlock />
  }

  if (diffQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load diff">
        {errorMessage(diffQuery.error)}
      </Banner>
    )
  }

  if (isDraftMode && validateQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load validation results">
        {errorMessage(validateQuery.error)}
      </Banner>
    )
  }

  const diff = diffQuery.data
  // Backend truth: does a draft edit exist for this file at all. Drives the
  // header count, the empty-state check, and Discard/Publish visibility -
  // Discard must stay reachable even for a file whose only "difference" is
  // the invisible one hasVisibleContent filters out below (see its own
  // comment), or that byte-level drift would have no way to be cleaned up
  // from the UI at all.
  const changedFiles = diff.files.filter((file) => file.status !== 'unchanged')
  const changedPrompts = diff.prompts.filter((prompt) => prompt.status !== 'unchanged')
  const changedCount = changedFiles.length + changedPrompts.length

  const errorsByFile = new Map<FileKey, ValidationError[]>()
  if (isDraftMode && validateQuery.data) {
    for (const file of validateQuery.data.files) {
      if (file.errors.length > 0) errorsByFile.set(file.fileKey, file.errors)
    }
  }

  function hasVisibleContent(file: (typeof diff.files)[number]): boolean {
    const hasErrors = (errorsByFile.get(file.fileKey)?.length ?? 0) > 0
    if (file.status === 'unchanged') return hasErrors
    // A "modified" status with an empty changes[] has nothing to show. This
    // is reachable for real: the backend's fast-path "identical?" check
    // compares canonical JSON text, but a value that round-tripped through
    // this app's own JS number type (e.g. a schema float like `0.0`) can
    // come back as `0` on save, making the byte-level text differ even
    // though every leaf is still semantically equal - so the leaf walk
    // underneath correctly finds zero real differences. Render it only if
    // it at least carries a validation error, rather than an empty,
    // confusing card. "added"/"removed" always carry exactly one synthetic
    // entry, so this never affects them.
    if (file.status === 'modified' && file.changes.length === 0) return hasErrors
    return true
  }

  const blocksToRender = diff.files.filter(hasVisibleContent)

  function anchorIdForError(fileKey: FileKey, path: string): string {
    const diffEntry = diff.files.find((file) => file.fileKey === fileKey)
    const hasMatchingRow = diffEntry?.changes.some((change) => change.path === path) ?? false
    return hasMatchingRow ? `${fileKey}-${path}` : `${fileKey}-block`
  }

  const jumpLinks: ValidationJumpLink[] = []
  if (isDraftMode && validateQuery.data) {
    for (const file of validateQuery.data.files) {
      for (const error of file.errors) {
        const fileLabel = fileKeyLabel(file.fileKey)
        const pathLabel = humanizePath(error.path)
        // Some file_keys' own content nests everything under a top-level
        // key matching the file_key name (document_types, product_types,
        // lookup_table) - humanizePath's first breadcrumb segment then
        // duplicates the file label. Others (clients' "firms",
        // global_defaults' "hard_caps"/"acceptance") don't, so the prefix
        // stays load-bearing there. Skip the prefix only when it would
        // literally repeat.
        jumpLinks.push({
          key: `${file.fileKey}-${error.path}`,
          label: pathLabel.startsWith(`${fileLabel} > `) ? pathLabel : `${fileLabel}: ${pathLabel}`,
          anchorId: anchorIdForError(file.fileKey, error.path),
        })
      }
    }
  }

  function handleJump(anchorId: string) {
    setHighlightedAnchor(anchorId)
    document.getElementById(anchorId)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current)
    highlightTimeoutRef.current = setTimeout(() => setHighlightedAnchor(null), HIGHLIGHT_DURATION_MS)
  }

  const isValid = validateQuery.data?.valid ?? false
  const publishDisabled = !isDraftMode || !isValid

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-title font-semibold text-text-primary">
            {isDraftMode ? 'Review changes before publishing' : `Comparing ${from} → ${to}`}
          </h1>
          <p className="text-body text-text-secondary">
            {changedCount} file{changedCount === 1 ? '' : 's'} changed
          </p>
        </div>
        {isDraftMode && changedCount > 0 && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={writesLocked}
              onClick={() => setDiscardModalOpen(true)}
              className="flex items-center gap-1.5 rounded-control border border-border px-4 py-2 text-body font-medium text-text-primary hover:border-danger/60 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Trash2 size={16} strokeWidth={1.5} />
              Discard changes
            </button>
            {publishDisabled ? (
              // aria-disabled (not the native `disabled` attribute) so the
              // Tooltip's hover/focus listeners keep firing - matches
              // ProductTypesTable.tsx's own established disabled-with-
              // tooltip convention.
              <Tooltip text="Fix validation issues before publishing">
                <button
                  type="button"
                  aria-disabled="true"
                  className="flex cursor-not-allowed items-center gap-1.5 rounded-control bg-accent/50 px-4 py-2 text-body font-medium text-white/70"
                >
                  <UploadCloud size={16} strokeWidth={1.5} />
                  Publish
                </button>
              </Tooltip>
            ) : (
              <button
                type="button"
                disabled={writesLocked}
                onClick={() => setPublishModalOpen(true)}
                className="flex items-center gap-1.5 rounded-control bg-accent px-4 py-2 text-body font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                <UploadCloud size={16} strokeWidth={1.5} />
                Publish
              </button>
            )}
          </div>
        )}
      </div>

      {isDraftMode && writesLocked && (
        <Banner variant="info" icon={AlertCircle} title="A save is still in progress">
          Discard and Publish are available again as soon as it has finished.
        </Banner>
      )}

      {isDraftMode && (
        <ValidationBanner valid={isValid} jumpLinks={jumpLinks} onJump={handleJump} />
      )}

      {changedCount === 0 ? (
        <EmptyState
          icon={FileCheck2}
          headline={isDraftMode ? 'No pending changes' : 'No changes between these versions'}
          description={
            isDraftMode
              ? 'Nothing in the current draft differs from the live bundle yet.'
              : `${from} and ${to} have identical configuration.`
          }
          action={{ label: 'Back to Dashboard', onClick: () => navigate('/') }}
        />
      ) : blocksToRender.length === 0 && changedPrompts.length === 0 ? (
        // changedCount > 0 but every file's difference is the invisible
        // kind hasVisibleContent filters out (see its own comment) - still
        // worth a line of explanation rather than silent blank space, since
        // Discard above is the real fix and stays visible/available.
        <p className="text-body text-text-secondary">
          {changedCount === 1 ? 'The 1 changed file has' : `All ${changedCount} changed files have`} no visible
          differences from the live bundle. Use Discard changes to clear it.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {blocksToRender.map((fileEntry) => (
            <DiffFileBlock
              key={fileEntry.fileKey}
              fileEntry={fileEntry}
              fileErrors={errorsByFile.get(fileEntry.fileKey) ?? []}
              highlightedAnchor={highlightedAnchor}
              onDiscardFile={isDraftMode && !writesLocked ? () => setFileToDiscard(fileEntry.fileKey) : undefined}
              onRevertField={
                isDraftMode && !writesLocked ? (change) => handleRevertField(fileEntry.fileKey, change) : undefined
              }
              revertingPath={
                revertingKey?.startsWith(`${fileEntry.fileKey}::`)
                  ? revertingKey.slice(`${fileEntry.fileKey}::`.length)
                  : null
              }
            />
          ))}
          {changedPrompts.map((promptEntry) => (
            <DiffPromptBlock
              key={promptEntry.filename}
              promptEntry={promptEntry}
              highlightedAnchor={highlightedAnchor}
              onDiscard={isDraftMode && !writesLocked ? () => setPromptToDiscard(promptEntry.filename) : undefined}
            />
          ))}
        </div>
      )}

      <ConfirmModal
        open={discardModalOpen}
        title="Discard changes?"
        description={
          changedCount === 1
            ? 'This removes the 1 changed file from the draft. This cannot be undone.'
            : `This removes all ${changedCount} changed files from the draft. This cannot be undone.`
        }
        confirmLabel={discardMutation.isPending ? 'Discarding…' : 'Discard'}
        onConfirm={() =>
          discardMutation.mutate({
            fileKeys: changedFiles.map((file) => file.fileKey),
            promptFilenames: changedPrompts.map((prompt) => prompt.filename),
          })
        }
        onCancel={() => setDiscardModalOpen(false)}
      />

      <ConfirmModal
        open={promptToDiscard !== null}
        title={promptToDiscard ? `Discard ${promptToDiscard}?` : ''}
        description={
          promptToDiscard
            ? `This removes the draft edit for ${promptToDiscard} only - other changed files are untouched. This cannot be undone.`
            : ''
        }
        confirmLabel={discardPromptMutation.isPending ? 'Discarding…' : 'Discard'}
        onConfirm={() => promptToDiscard && discardPromptMutation.mutate(promptToDiscard)}
        onCancel={() => setPromptToDiscard(null)}
      />

      <ConfirmModal
        open={fileToDiscard !== null}
        title={fileToDiscard ? `Discard ${fileKeyLabel(fileToDiscard)}?` : ''}
        description={
          fileToDiscard
            ? `This removes the draft edit for ${fileKeyLabel(fileToDiscard)} only - other changed files are untouched. This cannot be undone.`
            : ''
        }
        confirmLabel={discardFileMutation.isPending ? 'Discarding…' : 'Discard'}
        onConfirm={() => fileToDiscard && discardFileMutation.mutate(fileToDiscard)}
        onCancel={() => setFileToDiscard(null)}
      />

      <PublishModal
        open={publishModalOpen}
        liveVersion={liveVersion}
        releaseNote={releaseNote}
        onReleaseNoteChange={setReleaseNote}
        onConfirm={() => publishMutation.mutate(releaseNote)}
        onCancel={() => {
          setPublishModalOpen(false)
          publishMutation.reset()
          setReleaseNote('')
        }}
        onNavigateToDashboard={() => navigate('/')}
        isPending={publishMutation.isPending}
        isError={publishMutation.isError}
        error={publishMutation.error}
        isSuccess={publishMutation.isSuccess}
        result={publishMutation.data}
      />
    </div>
  )
}
